import { supabase } from '@/integrations/supabase/client';

export interface CustomerJourneyEvent {
  event_type: string;
  event_data?: Record<string, unknown>;
  page_url?: string;
  user_agent?: string;
  referrer?: string;
  session_id?: string;
}

export interface CustomerBehaviorAnalysis {
  page_views: number;
  product_views: number;
  cart_additions: number;
  orders_placed: number;
  conversion_rate: number;
  cart_conversion_rate: number;
  engagement_score: number;
}

export interface CustomerPreference {
  preference_type: string;
  preference_key: string;
  preference_value: unknown;
  is_active?: boolean;
}

export interface CustomerFeedback {
  feedback_type: 'rating' | 'review' | 'suggestion' | 'complaint';
  rating?: number;
  title?: string;
  content?: string;
  tags?: string[];
  order_id?: string;
  is_public?: boolean;
}

export interface NotificationData {
  notification_type: 'order_update' | 'promotion' | 'reminder' | 'system';
  title: string;
  content: string;
  action_url?: string;
  customer_id?: string;
}

class CustomerJourneyService {
  private sessionId: string | null = null;
  private userId: string | null = null;

  constructor() {
    this.initializeSession();
  }

  private initializeSession() {
    // Get or create session ID
    this.sessionId = sessionStorage.getItem('customer_session_id');
    if (!this.sessionId) {
      this.sessionId = this.generateSessionId();
      sessionStorage.setItem('customer_session_id', this.sessionId);
    }

    // Listen for auth changes
    supabase.auth.onAuthStateChange((event, session) => {
      this.userId = session?.user?.id || null;
      if (event === 'SIGNED_IN' && this.userId) {
        this.trackEvent('user_login', { method: 'auth' });
      } else if (event === 'SIGNED_OUT') {
        this.trackEvent('user_logout');
        this.userId = null;
      }
    });
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Track a customer journey event
   */
  async trackEvent(eventType: string, eventData: Record<string, unknown> = {}): Promise<void> {
    try {
      const event: CustomerJourneyEvent = {
        event_type: eventType,
        event_data: eventData,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        session_id: this.sessionId
      };

      if (this.userId) {
        // Use the database function for authenticated users
        await supabase.rpc('track_customer_event', {
          p_user_id: this.userId,
          p_event_type: eventType,
          p_event_data: eventData,
          p_session_id: this.sessionId
        });
      } else {
        // For anonymous users, store in localStorage temporarily
        this.storeAnonymousEvent(event);
      }
    } catch (error) {
      console.error('Error tracking customer journey event:', error);
    }
  }

  /**
   * Store anonymous events in localStorage
   */
  private storeAnonymousEvent(event: CustomerJourneyEvent): void {
    try {
      const events = JSON.parse(localStorage.getItem('anonymous_events') || '[]');
      events.push({ ...event, timestamp: new Date().toISOString() });
      
      // Keep only last 100 events
      if (events.length > 100) {
        events.splice(0, events.length - 100);
      }
      
      localStorage.setItem('anonymous_events', JSON.stringify(events));
    } catch (error) {
      console.error('Error storing anonymous event:', error);
    }
  }

  /**
   * Sync anonymous events when user logs in
   */
  async syncAnonymousEvents(): Promise<void> {
    if (!this.userId) return;

    try {
      const events = JSON.parse(localStorage.getItem('anonymous_events') || '[]');
      
      for (const event of events) {
        await supabase.rpc('track_customer_event', {
          p_user_id: this.userId,
          p_event_type: event.event_type,
          p_event_data: event.event_data || {},
          p_session_id: event.session_id
        });
      }

      // Clear anonymous events after sync
      localStorage.removeItem('anonymous_events');
    } catch (error) {
      console.error('Error syncing anonymous events:', error);
    }
  }

  /**
   * Get customer behavior analysis
   */
  async getBehaviorAnalysis(userId?: string, lookbackDays: number = 30): Promise<CustomerBehaviorAnalysis | null> {
    try {
      const targetUserId = userId || this.userId;
      if (!targetUserId) return null;

      const { data, error } = await supabase.rpc('analyze_customer_behavior', {
        target_user_id: targetUserId,
        lookback_days: lookbackDays
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting behavior analysis:', error);
      return null;
    }
  }

  /**
   * Calculate churn risk for a customer
   */
  async getChurnRisk(userId?: string): Promise<number> {
    try {
      const targetUserId = userId || this.userId;
      if (!targetUserId) return 0;

      const { data, error } = await supabase.rpc('calculate_churn_risk', {
        target_user_id: targetUserId
      });

      if (error) throw error;
      return data || 0;
    } catch (error) {
      console.error('Error calculating churn risk:', error);
      return 0;
    }
  }

  /**
   * Save customer preference
   */
  async savePreference(customerId: string, preference: CustomerPreference): Promise<void> {
    try {
      const { error } = await supabase
        .from('customer_preferences')
        .upsert({
          customer_id: customerId,
          preference_type: preference.preference_type,
          preference_key: preference.preference_key,
          preference_value: preference.preference_value,
          is_active: preference.is_active ?? true
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving customer preference:', error);
      throw error;
    }
  }

  /**
   * Get customer preferences
   */
  async getPreferences(customerId: string, preferenceType?: string): Promise<CustomerPreference[]> {
    try {
      let query = supabase
        .from('customer_preferences')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true);

      if (preferenceType) {
        query = query.eq('preference_type', preferenceType);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting customer preferences:', error);
      return [];
    }
  }

  /**
   * Submit customer feedback
   */
  async submitFeedback(customerId: string, feedback: CustomerFeedback): Promise<void> {
    try {
      const { error } = await supabase
        .from('customer_feedback')
        .insert({
          customer_id: customerId,
          order_id: feedback.order_id,
          feedback_type: feedback.feedback_type,
          rating: feedback.rating,
          title: feedback.title,
          content: feedback.content,
          tags: feedback.tags,
          is_public: feedback.is_public ?? false
        });

      if (error) throw error;

      // Track feedback submission
      await this.trackEvent('feedback_submitted', {
        feedback_type: feedback.feedback_type,
        rating: feedback.rating,
        order_id: feedback.order_id
      });
    } catch (error) {
      console.error('Error submitting customer feedback:', error);
      throw error;
    }
  }

  /**
   * Get customer feedback
   */
  async getFeedback(customerId: string, limit: number = 10): Promise<CustomerFeedback[]> {
    try {
      const { data, error } = await supabase
        .from('customer_feedback')
        .select('*, orders(order_number)')
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting customer feedback:', error);
      return [];
    }
  }

  /**
   * Create notification
   */
  async createNotification(userId: string, notification: NotificationData): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          customer_id: notification.customer_id,
          notification_type: notification.notification_type,
          title: notification.title,
          content: notification.content,
          action_url: notification.action_url
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  /**
   * Get user notifications
   */
  async getNotifications(userId: string, limit: number = 20): Promise<NotificationData[]> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting notifications:', error);
      return [];
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, read_at: new Date().toISOString() })
        .eq('id', notificationId);

      if (error) throw error;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Get comprehensive user view
   */
  async getUserComprehensiveView(userId: string): Promise<Record<string, unknown> | null> {
    try {
      const { data, error } = await supabase
        .from('user_comprehensive_view')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting user comprehensive view:', error);
      return null;
    }
  }

  /**
   * Refresh comprehensive view (for admins)
   */
  async refreshComprehensiveView(): Promise<void> {
    try {
      const { error } = await supabase.rpc('refresh_user_comprehensive_view');
      if (error) throw error;
    } catch (error) {
      console.error('Error refreshing comprehensive view:', error);
      throw error;
    }
  }

  // Convenience methods for common tracking events
  async trackPageView(pageName: string, pageData: Record<string, unknown> = {}): Promise<void> {
    return this.trackEvent('page_view', { page: pageName, ...pageData });
  }

  async trackProductView(productId: string, productData: Record<string, unknown> = {}): Promise<void> {
    return this.trackEvent('product_view', { product_id: productId, ...productData });
  }

  async trackAddToCart(productId: string, quantity: number, productData: Record<string, unknown> = {}): Promise<void> {
    return this.trackEvent('add_to_cart', { product_id: productId, quantity, ...productData });
  }

  async trackRemoveFromCart(productId: string, quantity: number): Promise<void> {
    return this.trackEvent('remove_from_cart', { product_id: productId, quantity });
  }

  async trackSearch(query: string, resultsCount: number, filters: Record<string, unknown> = {}): Promise<void> {
    return this.trackEvent('search', { query, results_count: resultsCount, filters });
  }

  async trackWishlistAdd(productId: string): Promise<void> {
    return this.trackEvent('wishlist_add', { product_id: productId });
  }

  async trackWishlistRemove(productId: string): Promise<void> {
    return this.trackEvent('wishlist_remove', { product_id: productId });
  }

  async trackCheckoutStart(orderData: Record<string, unknown>): Promise<void> {
    return this.trackEvent('checkout_start', orderData);
  }

  async trackPaymentAttempt(paymentMethod: string, amount: number): Promise<void> {
    return this.trackEvent('payment_attempt', { payment_method: paymentMethod, amount });
  }
}

// Export singleton instance
export const customerJourneyService = new CustomerJourneyService();
export default customerJourneyService;